import type { Session, User } from '@supabase/supabase-js';
import type { SupabaseAuthOnlyClient } from '../lib/supabaseAuth';

/**
 * Framework-agnostic auth state layer.
 *
 * This store owns the browser auth session state and wraps the auth-only Supabase
 * surface (`SupabaseAuthOnlyClient`). The React AuthProvider is a thin binding
 * over this store (via useSyncExternalStore), so the signed-in / signed-out /
 * loading logic is unit-testable without rendering.
 *
 * It never touches app data — only Supabase Auth (getSession, onAuthStateChange,
 * sign in / sign up / sign out, password reset).
 *
 * V2-018: a password-recovery session is NOT a normal authenticated session. It
 * arrives from an emailed link, it is not proof the holder knew a password, and it
 * exists for exactly one purpose: setting a new password. It is therefore given
 * its own status rather than being folded into `signed-in`, so every consumer that
 * gates on `signed-in` refuses it by default instead of opting out one at a time.
 */
export type AuthStatus =
  | 'loading'
  | 'signed-in'
  | 'signed-out'
  | 'password-recovery';

export interface AuthState {
  status: AuthStatus;
  session: Session | null;
  user: User | null;
}

/**
 * Result of reading the persisted recovery marker.
 *
 * `unavailable` is kept distinct from `absent` deliberately. They are treated
 * the same today (Policy A: neither is usable evidence, so the session is
 * classified normally), but the distinction is the whole cost of changing that
 * decision later if hosted evidence justifies it.
 */
export type RecoveryMarkerRead =
  | { kind: 'absent' }
  | { kind: 'present'; userId: string }
  | { kind: 'unavailable' };

/**
 * Persistence port for the recovery latch. Declared here so the store stays
 * DOM-free and node-testable; the browser implementation lives in
 * `recoveryMarker.ts` and is injected by AuthProvider.
 *
 * Implementations must not throw. The store guards every call regardless,
 * because a port it did not write must never be able to break sign-in.
 */
export interface RecoveryMarker {
  read(): RecoveryMarkerRead;
  set(userId: string): void;
  clear(): void;
}

export interface AuthStore {
  /** Current snapshot; stable reference until the next auth change. */
  getState(): AuthState;
  /** Subscribe to state changes; returns an unsubscribe function. */
  subscribe(listener: () => void): () => void;
  /** Begin side effects (load session + subscribe). Idempotent; re-startable after dispose(). */
  start(): void;
  signInWithPassword: SupabaseAuthOnlyClient['auth']['signInWithPassword'];
  signUp: SupabaseAuthOnlyClient['auth']['signUp'];
  signOut: SupabaseAuthOnlyClient['auth']['signOut'];
  /** Send a recovery email. Resolves the same way whether or not the address exists. */
  resetPasswordForEmail: SupabaseAuthOnlyClient['auth']['resetPasswordForEmail'];
  /** Set a new password for the current (recovery) session. */
  updateUser: SupabaseAuthOnlyClient['auth']['updateUser'];
  /** Stop side effects and clear listeners. Safe before start() and if called twice. */
  dispose(): void;
}

const LOADING: AuthState = { status: 'loading', session: null, user: null };
const SIGNED_OUT: AuthState = { status: 'signed-out', session: null, user: null };

function stateFromSession(session: Session | null, recovering: boolean): AuthState {
  if (!session) {
    return SIGNED_OUT;
  }
  return {
    status: recovering ? 'password-recovery' : 'signed-in',
    session,
    user: session.user,
  };
}

export function createAuthStore(
  client: SupabaseAuthOnlyClient,
  /**
   * Optional. Omitted, the latch is in-memory only and behaves exactly as it did
   * before Tier 2, which is why every pre-existing test remains valid unchanged.
   */
  marker?: RecoveryMarker,
): AuthStore {
  // Construction is pure: no getSession()/onAuthStateChange() until start().
  let state: AuthState = LOADING;
  let active = false;
  let generation = 0;
  // Latched on PASSWORD_RECOVERY. Supabase fires that event exactly once, so
  // without the latch the next TOKEN_REFRESHED would carry the same session and
  // silently promote it to `signed-in`.
  //
  // Exactly three things clear it:
  //   - a null session (signed out), in setSession below;
  //   - the updateUser wrapper, once Supabase accepts a password it sent;
  //   - the signInWithPassword wrapper, on a proven password sign-in.
  // No auth event clears it; the reasoning is in the onAuthStateChange handler.
  //
  // Deliberately NOT reset by dispose(): the store instance survives React Strict
  // Mode's setup → cleanup → setup, and Supabase does not re-fire the event, so
  // resetting here would drop the user out of recovery on a dev remount.
  let recovering = false;
  let subscription: { unsubscribe: () => void } | null = null;
  const listeners = new Set<() => void>();

  // Every marker call is guarded here as well as in the implementation. A port
  // this store did not write must never be able to break auth initialisation, so
  // a throw is degraded to `unavailable` rather than propagated.
  const readMarker = (): RecoveryMarkerRead => {
    // No port is `absent`, not `unavailable`: persistence was never configured,
    // which is a different thing from storage that exists and failed. The
    // distinction matters if the unavailable policy is ever tightened.
    if (!marker) return { kind: 'absent' };
    try {
      return marker.read();
    } catch {
      return { kind: 'unavailable' };
    }
  };
  const writeMarker = (userId: string): void => {
    try {
      marker?.set(userId);
    } catch {
      /* durability across a reload is lost; the in-memory latch still holds */
    }
  };
  const clearMarker = (): void => {
    try {
      marker?.clear();
    } catch {
      /* a surviving marker over-restricts, which is the safe direction */
    }
  };

  const emit = (): void => {
    for (const listener of listeners) {
      listener();
    }
  };

  const setSession = (
    session: Session | null,
    token: number,
    /**
     * Marker consultation is opt-in, and only the initial getSession() restore
     * opts in. Defaulting it on would let any later auth event resurrect a
     * marker that a controlled path failed to clear: a throwing clear() would
     * survive as a stale value, and the next TOKEN_REFRESHED would re-latch a
     * session whose password had already been successfully set.
     */
    options: { restoreFromMarker?: boolean } = {},
  ): void => {
    // Ignore stale results from a superseded start()/dispose() cycle.
    if (token !== generation) {
      return;
    }
    // No session means no recovery to finish: signing out ends it.
    if (!session) {
      recovering = false;
      clearMarker();
    } else if (!recovering && options.restoreFromMarker) {
      // Restore path. A reload loses the in-memory latch, and Supabase replays
      // the session as an ordinary SIGNED_IN, so the marker is the only evidence
      // left that this session came from a recovery link.
      const marked = readMarker();
      if (marked.kind === 'present') {
        if (marked.userId === session.user.id) {
          recovering = true;
        } else {
          // Belongs to a different user: stale. Drop it and classify normally.
          clearMarker();
        }
      }
      // `absent` and `unavailable` alike are not usable evidence, so the session
      // is classified normally (Policy A). auth-js falls back to in-memory
      // session storage when localStorage is unavailable, so a *restored*
      // session cannot normally coexist with an unavailable marker.
    }
    state = stateFromSession(session, recovering);
    emit();
  };

  const start = (): void => {
    if (active) {
      return;
    }
    active = true;
    const token = ++generation;

    // Initial load. On an error field or a rejected promise, fall back to
    // signed-out so the store never stays stuck in `loading`. No session data or
    // token is ever logged.
    void client.auth
      .getSession()
      .then(({ data, error }) => {
        // The only marker-consulting call site: this is the restored session.
        setSession(error ? null : data.session, token, { restoreFromMarker: true });
      })
      .catch(() => {
        setSession(null, token);
      });

    const {
      data: { subscription: sub },
    } = client.auth.onAuthStateChange((event, session) => {
      // The event is read, not discarded: a recovery session is indistinguishable
      // from a normal one by inspecting the session alone, so this is the only
      // point at which the difference is observable.
      //
      // Only PASSWORD_RECOVERY writes the latch. USER_UPDATED deliberately does
      // NOT clear it: that event also fires for email and metadata changes, so
      // treating it as proof of a completed reset would promote a recovery
      // session to `signed-in` with no password ever having been set. The latch
      // is cleared by the updateUser wrapper below, which knows what it sent.
      if (event === 'PASSWORD_RECOVERY') {
        recovering = true;
        // Persist the durable half. Only the user id: it is not a credential,
        // and it is only ever compared against an already-verified session.
        if (session) writeMarker(session.user.id);
      }
      setSession(session, token);
    });
    subscription = sub;
  };

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    start,
    // Bounded wrapper for the same reason as updateUser: proving the password is
    // proof this is an ordinary session, whatever a stale marker claims.
    //
    // @supabase/auth-js 2.108.2 notifies subscribers of SIGNED_IN before this
    // method resolves, so the snapshot may already have been re-latched by the
    // time control returns here; clearing the latch alone would not repair it.
    // The state is therefore re-derived through setSession.
    signInWithPassword: async (...args) => {
      const token = generation;
      const result = await client.auth.signInWithPassword(...args);

      const signedInUserId = result.data.session?.user.id ?? null;
      if (
        result.error ||
        token !== generation ||
        !signedInUserId ||
        state.session?.user.id !== signedInUserId
      ) {
        return result;
      }

      recovering = false;
      clearMarker();
      setSession(state.session, token);

      return result;
    },
    signUp: (...args) => client.auth.signUp(...args),
    signOut: (...args) => client.auth.signOut(...args),
    resetPasswordForEmail: (...args) => client.auth.resetPasswordForEmail(...args),
    // Bounded wrapper, not a raw delegation: this is the only place that can
    // prove a password was set, because it sent the attributes and saw the
    // result.
    //
    // Every lifecycle and session check happens BEFORE the latch is touched. An
    // update that outlived its store generation, or whose session changed while
    // it was in flight, must not clear recovery for whatever session is current
    // now. Clearing first and validating afterwards would leave the latch
    // mutated even when the write is rejected.
    updateUser: async (...args) => {
      const token = generation;
      const recoveryUserId =
        recovering && state.session ? state.session.user.id : null;
      const [attributes] = args;

      const result = await client.auth.updateUser(...args);

      if (
        result.error ||
        typeof attributes.password !== 'string' ||
        token !== generation ||
        !recovering ||
        !recoveryUserId ||
        state.session?.user.id !== recoveryUserId
      ) {
        return result;
      }

      recovering = false;
      // Cleared before setSession, or the restore path would read the marker it
      // still matches and re-latch the session that was just completed.
      clearMarker();

      // @supabase/auth-js 2.108.2 notifies subscribers of USER_UPDATED before
      // this method resolves, so that event was handled while the latch was
      // still set. Re-derive through setSession, the store's single writer.
      setSession(state.session, token);

      return result;
    },
    dispose() {
      active = false;
      generation += 1;
      if (subscription) {
        subscription.unsubscribe();
        subscription = null;
      }
      listeners.clear();
    },
  };
}
